import React, { useMemo, useState } from 'react';
import { useWorkplacesSuklCodeQuery } from '../../../../../../graphql/graphql';
import Loading from '../../../../../../Components/Loading';
import MagicForm from '../../../../../../Components/Form/MagicForm';
import { ItemScheme } from '../../../../../../Components/Form/MagicFormItem';
import { MagicField } from '../../../../../../Enums/MagicField.enum';
import apiClient from '../../../../../../Services/ApiClient';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import useNotification from '../../../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../../../Hooks/useTranslation';

type ManagerHookUpSUKLFormValues = Record<
	string,
	{ add: boolean; suklCode?: string }
>;

interface Props {
	onSave: () => void;
}

const ManagerHookUpSUKL: React.FC<Props> = ({ onSave }) => {
	const { displayError } = useApiClient();
	const { showNotificationAlert } = useNotification();
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const {
		data,
		isLoading: isDataLoading,
		refetch,
	} = useWorkplacesSuklCodeQuery();

	const formScheme: ItemScheme[] = useMemo(() => {
		const temp: ItemScheme[] = [];
		data?.workplaces?.data?.forEach(workplace => {
			temp.push({
				type: MagicField.CONTAINER,
				key: workplace?.id!,
				children: [
					{
						type: MagicField.CHECKBOX,
						key: 'add',
						title: workplace?.name!,
					},
					{
						type: MagicField.CONDITION,
						settings: {
							rules: [
								{
									formControlKey: `${workplace?.id}.add`,
									rule: 'equal',
									value: true,
								},
							],
						},
						subType: 'view',
						children: [
							{
								type: MagicField.TEXT_FIELD,
								key: 'suklCode',
								title: 'component.certificates.workplaceCodeSUKL',
								settings: {
									required: true,
								},
							},
						],
					},
				],
			});
		});
		return temp;
	}, [data]);

	const defaultValues: ManagerHookUpSUKLFormValues = useMemo(() => {
		const temp: ManagerHookUpSUKLFormValues = {};

		data?.workplaces?.data?.forEach(workplace => {
			if (workplace) {
				const suklCode = workplace.meta?.find(item => item?.key === 'suklCode');
				temp[workplace.id] = {
					add: !!suklCode,
					suklCode: suklCode?.value ?? undefined,
				};
			}
		});

		return temp;
	}, [data]);

	const handleSubmit = async (values: ManagerHookUpSUKLFormValues) => {
		setIsLoading(true);
		const requests = Object.entries(values).map(
			async ([workplaceId, { add, suklCode }]) => {
				try {
					if (add) {
						const response = await apiClient.post(
							`/workplaces/${workplaceId}/meta`,
							{
								key: 'suklCode',
								value: suklCode,
							},
						);
						return response.status;
					} else if (defaultValues[workplaceId].suklCode) {
						const response = await apiClient.delete(
							`/workplaces/${workplaceId}/meta/suklCode`,
						);
						return response.status;
					} else {
						return 200;
					}
				} catch (e) {
					displayError(e);
					return 500;
				}
			},
		);

		const statuses = await Promise.all(requests);

		if (statuses.every(status => status === 200)) {
			await refetch();
			onSave();
		} else {
			showNotificationAlert({ content: t('base.shared.warningBug') });
		}
		setIsLoading(false);
	};

	if (isDataLoading || isLoading) {
		return <Loading />;
	}

	return (
		<MagicForm
			formScheme={formScheme}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
		/>
	);
};

export default ManagerHookUpSUKL;
